import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox as MuiCheckbox,
  FormControlLabel as MuiFormControlLabel,
  Grid,
  Link,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import clsx from "clsx";
import countryRegionData, { Country, Region } from "country-region-data";
import { Form, Formik, FormikProps } from "formik";
import { find } from "lodash";
import * as yup from "yup";

import BLACKED_DOMAINS from "black-domains";
import Alert from "components/shared/Alert";
import FormikFormField, {
  FormikAutoComplete,
} from "components/shared/FormikFormField";
import PasswordToggleIcon from "components/shared/PasswordToggleIcon";
import { REGISTER } from "helpers/apis";
import useAxios from "helpers/axios";
import useDialog from "hooks/useDialog";
import { colors } from "theme";
import { IForm, errors, passwordValidation } from "types/form";

interface IRegisterForm extends IForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  jobTitle: string;
  companyName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  agreement: boolean;
}

const detailsOfAgreement: string[] = [
  "you are at least 18 years of age;",
  "you are using your actual identity;",
  "you have provided only true, accurate, current and complete information about yourself during the registration process;",
  "you will maintain and promptly update the information that you provide to keep it true, accurate, current and complete; and",
  "you will have obtained registration and will use our Website only for the activities permitted by such registration.",
];

const validationSchema = yup.object({
  firstName: yup.string().required(errors.required),
  lastName: yup.string().required(errors.required),
  email: yup
    .string()
    .email(errors.invalidEmail)
    .required(errors.required)
    .test(
      "domain-validation",
      "Must use company/institution email address.",
      (email: any) => {
        const domain = email.split("@")[1];
        return (
          !!domain &&
          !find(
            BLACKED_DOMAINS,
            (blackedDomain: string) => blackedDomain === domain
          )
        );
      }
    ),
  password: passwordValidation,
  phone: yup
    .string()
    .matches(
      /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      errors.invalidPhone
    )
    .required(errors.required),
  jobTitle: yup.string().required(errors.required),
  companyName: yup.string().required(errors.required),
  address1: yup.string().required(errors.required),
  address2: yup.string(),
  city: yup.string().required(errors.required),
  state: yup.string().required(errors.required),
  country: yup.string().required(errors.required),
  postalCode: yup.string().required(errors.required),
  agreement: yup
    .boolean()
    .required(errors.required)
    .oneOf([true], "You must agree on them first."),
});

const useStyles = makeStyles((theme: Theme) => ({
  agreement: {
    fontSize: 10,
    lineHeight: 1.5,
    letterSpacing: 1,
    color: colors.nero[300],
    "& li": { fontFamily: "'Futura Allele', sans-serif" },
  },
  detailsOfAgreement: {
    margin: 0,
    paddingLeft: 18,
    listStyleType: "disc",
  },
  withoutAgreement: {
    color: theme.palette.error.main,
  },
  passwordRequirements: {
    marginTop: 24,
  },
}));

const RegisterForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const {
    openRegisterDialog,
    setOpenLoginDialog,
    setOpenRegisterDialog,
    setOpenRegisterSuccessDialog,
  } = useDialog();
  const [visiblePassword, setVisiblePassword] = useState<boolean>(false);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState<boolean>(false);
  const isMobile =
    useMediaQuery((theme: Theme) => theme.breakpoints.down("sm")) ||
    openRegisterDialog;
  const inNarrowMode: boolean =
    useMediaQuery((theme: Theme) => theme.breakpoints.down("md")) ||
    openRegisterDialog;
  const columnWidth = inNarrowMode ? 12 : 6;
  const columnWidth1 = openRegisterDialog ? 12 : inNarrowMode ? 6 : 3;

  const { fetchData, loading } = useAxios({
    config: { url: REGISTER, method: "POST" },
    callOnInit: false,
  });

  const onSubmit = (values: IRegisterForm) => {
    fetchData({ data: values }, () => {
      if (openRegisterDialog) {
        setOpenRegisterDialog(false);
        setOpenRegisterSuccessDialog(true);
      } else {
        history.push("/register-success");
      }
    });
  };

  const handleLogin = useCallback(() => {
    if (openRegisterDialog) {
      setOpenLoginDialog(true);
      setOpenRegisterDialog(false);
    } else {
      history.push("/login");
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [openRegisterDialog]);

  const SignInLink = (
    <Typography variant="body1">
      Already have an account?{" "}
      <Link onClick={handleLogin} color="secondary" underline="always">
        Sign in.
      </Link>
    </Typography>
  );

  return (
    <Formik
      initialValues={{
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phone: "",
        jobTitle: "",
        companyName: "",
        address1: "",
        city: "",
        state: "",
        country: "",
        postalCode: "",
        agreement: false,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {(props: FormikProps<IRegisterForm>) => {
        const {
          dirty,
          errors,
          isValid,
          touched,
          values,
          handleChange,
          handleBlur,
        } = props;

        return (
          <Box className="register-form">
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="h2">Register an account</Typography>
              {!inNarrowMode && SignInLink}
            </Grid>
            <Box mt={2.625}>
              <Form>
                <Grid container spacing={3}>
                  <Grid item md={columnWidth} xs={12}>
                    <FormikFormField<IRegisterForm>
                      name="firstName"
                      {...props}
                    />
                  </Grid>
                  <Grid item md={columnWidth} xs={12}>
                    <FormikFormField name="lastName" {...props} />
                  </Grid>
                  <Grid item md={columnWidth} xs={12}>
                    <FormikFormField
                      name="email"
                      label="Email Address"
                      type="email"
                      {...props}
                    />
                  </Grid>
                  <Grid item md={columnWidth} xs={12}>
                    <FormikFormField
                      name="phone"
                      type="tel"
                      label="Phone Number"
                      {...props}
                      textFieldProps={{
                        helperText: "Use only numbers, spaces and dashes",
                      }}
                    />
                  </Grid>
                  <Grid item md={columnWidth} xs={12}>
                    <FormikFormField
                      name="password"
                      type={visiblePassword ? "text" : "password"}
                      {...props}
                      textFieldProps={{
                        InputProps: {
                          endAdornment: (
                            <PasswordToggleIcon
                              visible={visiblePassword}
                              setVisible={setVisiblePassword}
                            />
                          ),
                          onBlur: (e) => {
                            handleBlur(e);
                            setShowPasswordRequirements(false);
                          },
                          onFocus: () => setShowPasswordRequirements(true),
                        },
                      }}
                    />
                    {showPasswordRequirements && (
                      <Alert
                        severity="info"
                        isFullWidthText
                        noDismiss
                        icon={false}
                        className={classes.passwordRequirements}
                      >
                        Requirements for creating a new and strong password:
                        <ul>
                          <li>Must contain at least 9 characters.</li>
                          <li>
                            Must contain 1 uppercase letter, 1 lowercase letter,
                            1 number, and 1 special character from the
                            following: @$!%*#?&.
                          </li>
                        </ul>
                      </Alert>
                    )}
                  </Grid>
                </Grid>

                <Box mt={5} mb={3}>
                  <Typography variant="subtitle1" component="h3">
                    Please include company/institution for account approval.
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item md={columnWidth} xs={12}>
                    <FormikFormField name="jobTitle" {...props} />
                  </Grid>
                  <Grid item md={columnWidth} xs={12}>
                    <FormikFormField name="companyName" {...props} />
                  </Grid>
                  <Grid item md={columnWidth} xs={12}>
                    <FormikFormField
                      name="address1"
                      label="Address"
                      {...props}
                    />
                  </Grid>
                  <Grid item md={columnWidth} xs={12}>
                    <FormikFormField
                      name="address2"
                      label="Address Line 2 (optional)"
                      required={false}
                      {...props}
                    />
                  </Grid>
                  <Grid item sm={columnWidth1} xs={12}>
                    <FormikAutoComplete<IRegisterForm, Country>
                      name="country"
                      {...props}
                      options={countryRegionData}
                      textFieldName="countryName"
                      valueFieldName="countryShortCode"
                    />
                  </Grid>
                  <Grid item sm={columnWidth1} xs={12}>
                    <FormikAutoComplete<IRegisterForm, Region>
                      name="state"
                      {...props}
                      options={
                        values["country"]
                          ? countryRegionData.find(
                              (country: Country) =>
                                country.countryShortCode === values["country"]
                            )?.regions || []
                          : [{ name: "No States", shortCode: "" }]
                      }
                      textFieldName="name"
                      valueFieldName="shortCode"
                    />
                  </Grid>
                  <Grid item sm={columnWidth1} xs={12}>
                    <FormikFormField name="city" {...props} />
                  </Grid>
                  <Grid item sm={columnWidth1} xs={12}>
                    <FormikFormField name="postalCode" {...props} />
                  </Grid>
                  <Grid item md={columnWidth} xs={12}>
                    <Box ml={inNarrowMode ? 0 : 2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.agreement}
                            onChange={handleChange}
                            name="agreement"
                            color="primary"
                            className={clsx(
                              errors["agreement"] && touched["agreement"]
                                ? classes.withoutAgreement
                                : ""
                            )}
                          />
                        }
                        label={
                          <Typography variant="subtitle1">
                            I agree to IDT's{" "}
                            <Link
                              target="_blank"
                              href="https://www.idtdna.com/pages/support/usage-warranty-and-licenses"
                              color="secondary"
                              underline="always"
                            >
                              Terms of Use
                            </Link>{" "}
                            and{" "}
                            <Link
                              target="_blank"
                              href="https://www.idtdna.com/pages/home/general-information/privacy"
                              color="secondary"
                              underline="always"
                            >
                              Privacy Policy
                            </Link>
                            .
                          </Typography>
                        }
                        sx={{ alignItems: "center !important" }}
                      />
                    </Box>
                    <Box
                      mt={2}
                      ml={{ xs: 0, sm: openRegisterDialog ? 0 : 4.5 }}
                    >
                      <Typography className={classes.agreement}>
                        Legal Disclaimer text: Access to certain areas of our
                        Website is limited by a user identifier (“User ID”) and
                        password, which are selected and/or supplied as part of
                        the registration for a service account with IDT.
                      </Typography>
                      <Box mt={2}>
                        <Typography className={classes.agreement}>
                          By registering, you represent, warrant and covenant
                          that:
                        </Typography>
                        <ul
                          className={clsx(
                            classes.agreement,
                            classes.detailsOfAgreement
                          )}
                        >
                          {detailsOfAgreement.map(
                            (detailOfAgreement: string, index: number) => (
                              <li key={index}>{detailOfAgreement}</li>
                            )
                          )}
                        </ul>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      width={1}
                      justifyContent={inNarrowMode ? "center" : "flex-start"}
                      alignItems={inNarrowMode ? "center" : "flex-start"}
                      display="flex"
                      flexDirection="column"
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={loading || !dirty || !isValid}
                        type="submit"
                        size="large"
                        fullWidth={isMobile}
                      >
                        Submit for account approval
                      </Button>
                      {inNarrowMode && <Box mt={2}>{SignInLink}</Box>}
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
};

export default RegisterForm;

const Checkbox = styled(MuiCheckbox)({
  padding: 2,
  marginRight: 8,
});

const FormControlLabel = styled(MuiFormControlLabel)({
  margin: 0,
  alignItems: "flex-start",
});
